import { FC, useEffect, useState } from 'react';
import { observer, saveAs, useNavigate } from '~/modules';
import { Routes } from '~/routes';
import {
  EventTypes,
  EventTypesPt,
  ListPagination,
  formatToApiDate,
  getClientDetailedInfo,
  parseEnumToPt,
  showAlert,
  useStores,
} from '~/utils';
import Audit from './Audit';

export const AuditContainer: FC = () => {
  const navigate = useNavigate();
  const {
    audit: {
      client,
      logs,
      filters,
      currentPage,
      setCurrentPage,
      setFilters,
      getClientLogs,
      setCurrentLog,
      downloadClientLogs,
    },
  } = useStores();

  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<{
    startDate?: CalendarDay;
    endDate?: CalendarDay;
  }>({ startDate: undefined, endDate: undefined });

  const handleNavigate = (route: Routes) => navigate(route);

  const navigateToLogDetails = (log: ClientLog) => {
    setCurrentLog(log);
    handleNavigate(Routes.AUDIT_DETAILS);
  };

  const onClearSelectedPeriod = async () => {
    setSelectedDate({
      ...selectedDate,
      startDate: undefined,
      endDate: undefined,
    });
    setFilters({ ...filters, startDate: undefined, endDate: undefined });
    await fetchClientLogs();
  };

  const eventTypeOptions = Object.entries(EventTypes).map(([key, value]) => ({
    label: parseEnumToPt(key, EventTypesPt),
    value: value,
  }));

  const productOptions: ProductOptions = client.products
    ? client.products.map((product) => ({
        label: product.name,
        value: product.id,
      }))
    : [];

  const formatStartEndDate = () => {
    let startDate = '';
    let endDate = '';
    if (filters.startDate) {
      startDate = formatToApiDate(filters.startDate.dayDate.toDate());
      if (filters.endDate) {
        endDate = formatToApiDate(filters.endDate.dayDate.toDate());
      }
    }
    return [startDate, endDate];
  };

  const fetchClientLogs = async (
    page = currentPage || ListPagination.INITIAL_PAGE,
  ) => {
    try {
      setLoading(true);
      const [formattedStartDate, formattedEndDate] = formatStartEndDate();
      await getClientLogs({
        document: client.document,
        eventType: filters.eventType.map((item) => item.value).join(','),
        product: filters.product.map((item) => item.value).join(','),
        startDate: formattedStartDate || '',
        endDate: formattedEndDate || '',
        page,
      });
      setCurrentPage(page);
    } catch ({ message }) {
      showAlert({ message });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadClientLogs = async () => {
    try {
      const [formattedStartDate, formattedEndDate] = formatStartEndDate();
      const response = await downloadClientLogs({
        document: client.document,
        eventType: filters.eventType.map((item) => item.value).join(','),
        product: filters.product.map((item) => item.value).join(','),
        startDate: formattedStartDate || '',
        endDate: formattedEndDate || '',
      });
      const filename = response.headers
        .get('Content-Disposition')
        .split(';')
        .find((n) => n.includes('filename='))
        .replace('filename=', '')
        .trim();
      saveAs(response.data, filename);
    } catch ({ message }) {
      showAlert({ message });
    }
  };

  const handleDatePickerSearch = () => {
    setFilters({
      ...filters,
      startDate: selectedDate.startDate,
      endDate: selectedDate.endDate,
    });
  };

  useEffect(() => {
    fetchClientLogs();
  }, [filters]);

  return (
    <Audit
      clientName={client.fullName}
      isClientAppAccessBlocked={client.isAppAccessBlocked}
      clientDetailedInfo={getClientDetailedInfo(client)}
      productOptions={productOptions}
      eventTypeOptions={eventTypeOptions}
      logs={logs}
      isLoading={loading}
      navigateToLogDetails={navigateToLogDetails}
      filters={filters}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
      setFilters={setFilters}
      onClearSelectedPeriod={onClearSelectedPeriod}
      onChangePage={fetchClientLogs}
      onDownloadClientLogs={handleDownloadClientLogs}
      onDatePickerSearch={handleDatePickerSearch}
    />
  );
};

export default observer(AuditContainer);
